import { useState, useCallback } from 'react';
import { employeeDocumentsAPI } from '../api/employee-documents';

/**
 * Hook personalizado para gestión documental de empleados
 *
 * Características:
 * - CRUD completo de documentos
 * - Workflow de aprobación
 * - Versionamiento
 * - Descarga con auditoría
 * - Estadísticas y compliance
 */
export const useEmployeeDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ═══════════════════════════════════════════════════════════════
  // CARGAR DOCUMENTOS
  // ═══════════════════════════════════════════════════════════════

  const loadDocuments = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.getAll(filters);
      if (data.success) {
        setDocuments(data.documents || []);
        return { success: true, documents: data.documents };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Error al cargar documentos';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDocument = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.getById(id);
      return { success: data.success, document: data.document, error: data.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEmployeeDocuments = useCallback(async (employeeId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.getEmployeeDocuments(employeeId);
      if (data.success) {
        return { success: true, documents: data.documents };
      } else {
        setError(data.error);
        return { success: false, error: data.error };
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // CRUD OPERACIONES
  // ═══════════════════════════════════════════════════════════════

  const createDocument = useCallback(async (documentData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.create(documentData);
      return { success: data.success, document: data.document, error: data.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateDocument = useCallback(async (id, documentData) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.update(id, documentData);
      return { success: data.success, document: data.document, error: data.error };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const archiveDocument = useCallback(async (id, motivo) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.archive(id, motivo);
      return { success: data.success };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // WORKFLOW DE APROBACIÓN
  // ═══════════════════════════════════════════════════════════════

  const submitDocument = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.submit(id);
      return { success: data.success, message: data.message };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const approveDocument = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.approve(id);
      return { success: data.success, message: data.message };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const rejectDocument = useCallback(async (id, motivo) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.reject(id, motivo);
      return { success: data.success, message: data.message };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // VERSIONAMIENTO
  // ═══════════════════════════════════════════════════════════════

  const loadVersions = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.getVersions(id);
      return { success: data.success, versions: data.versions };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadNewVersion = useCallback(async (id, file, motivo) => {
    setLoading(true);
    setError(null);
    try {
      const data = await employeeDocumentsAPI.uploadNewVersion(id, file, motivo);
      return { success: data.success, document: data.document, message: data.message };
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // DESCARGAS
  // ═══════════════════════════════════════════════════════════════

  const downloadDocument = useCallback(async (id, nombre) => {
    try {
      const response = await employeeDocumentsAPI.download(id);
      // response.data ya es un Blob con el Content-Type correcto — no envolver
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombre || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (err) {
      const errorMsg = 'Error al descargar documento';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, []);

  const loadDownloadLog = useCallback(async (id) => {
    try {
      const data = await employeeDocumentsAPI.getDownloadLog(id);
      return { success: data.success, downloads: data.downloads };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // ALERTAS Y VENCIMIENTOS
  // ═══════════════════════════════════════════════════════════════

  const loadPendingAlerts = useCallback(async () => {
    try {
      const data = await employeeDocumentsAPI.getPendingAlerts();
      return { success: data.success, documents: data.documents };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const loadExpired = useCallback(async () => {
    try {
      const data = await employeeDocumentsAPI.getExpired();
      return { success: data.success, documents: data.documents };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // CATÁLOGOS
  // ═══════════════════════════════════════════════════════════════

  const loadCategorias = useCallback(async () => {
    try {
      const data = await employeeDocumentsAPI.getCategorias();
      return { success: data.success, categorias: data.categorias };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const loadTipos = useCallback(async (categoriaId) => {
    try {
      const data = await employeeDocumentsAPI.getTipos(categoriaId);
      return { success: data.success, tipos: data.tipos };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const loadTipo = useCallback(async (codigo) => {
    try {
      const data = await employeeDocumentsAPI.getTipo(codigo);
      return { success: data.success, tipo: data.tipo };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // ESTADÍSTICAS
  // ═══════════════════════════════════════════════════════════════

  const loadStats = useCallback(async () => {
    try {
      const data = await employeeDocumentsAPI.getStats();
      return { success: data.success, stats: data.stats };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const loadCompliance = useCallback(async () => {
    try {
      const data = await employeeDocumentsAPI.getCompliance();
      return {
        success: data.success,
        compliance: data.compliance,
        total_empleados: data.total_empleados,
        empleados_incompletos: data.empleados_incompletos
      };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  return {
    // State
    documents,
    loading,
    error,

    // CRUD
    loadDocuments,
    loadDocument,
    loadEmployeeDocuments,
    createDocument,
    updateDocument,
    archiveDocument,

    // Workflow
    submitDocument,
    approveDocument,
    rejectDocument,

    // Versiones
    loadVersions,
    uploadNewVersion,

    // Descargas
    downloadDocument,
    loadDownloadLog,

    // Alertas
    loadPendingAlerts,
    loadExpired,

    // Catálogos
    loadCategorias,
    loadTipos,
    loadTipo,

    // Estadísticas
    loadStats,
    loadCompliance
  };
};

export default useEmployeeDocuments;
